/**
 * Planned vs. booked hours per organisation, room and month (H-14, #1811) —
 * the export of the allocations tab. Pure apart from the cell escaping, so it
 * is testable; built from the JSON `/admin/allocation-windows/utilisation`
 * returns, as the other admin exports are.
 */

import { escapeCSVField } from './csvFieldEscape';
import { UtilisationReport, UtilisationRow } from '../api/organization/allocationWindowTypes';

export interface UtilisationCsvLabels {
    organization: string;
    room: string;
    month: string;
    kind: string;
    plannedHours: string;
    releasedHours: string;
    bookedHours: string;
    bookedInWindowHours: string;
    bookedOutsideWindowHours: string;
    shareUsed: string;
    contractedHours: string;
    /** For a cell that does not apply to the row (a dedicated room's share). */
    notApplicable: string;
    /** For a name that is not there. */
    missing: string;
    kindLabel: (kind: UtilisationRow['kind']) => string;
}

/** Decimal comma, as German Excel reads a number. */
const amount = (n: number): string => n.toFixed(2).replace('.', ',');

export const buildUtilisationCsv = (report: UtilisationReport, labels: UtilisationCsvLabels): string => {
    const header = [
        labels.organization, labels.room, labels.month, labels.kind,
        labels.plannedHours, labels.releasedHours, labels.bookedHours,
        labels.bookedInWindowHours, labels.bookedOutsideWindowHours, labels.shareUsed, labels.contractedHours,
    ];
    const orNa = (n: number | null): string => (n === null ? labels.notApplicable : amount(n));
    const lines = report.rows.map((r) => [
        r.organizationName ?? labels.missing,
        r.groupName ?? labels.missing,
        r.month,
        labels.kindLabel(r.kind),
        orNa(r.plannedHours),
        orNa(r.releasedHours),
        amount(r.bookedHours),
        orNa(r.bookedInWindowHours),
        orNa(r.bookedOutsideWindowHours),
        r.shareUsed === null ? labels.notApplicable : amount(r.shareUsed * 100),
        orNa(r.contractedHours),
    ]);
    return [header, ...lines].map((cells) => cells.map(escapeCSVField).join(';')).join('\n');
};

export const utilisationCsvFileName = (label: string, from: string, to: string): string => `${label}-${from}-${to}.csv`;
